import React, { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LinearProgress } from "@mui/material";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import { styled } from "@mui/material/styles";
import DragNdrop from "./DragNdrop";
import axios from "axios";
import { IoWarningOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import AppButton from "../../shared/AppButton";

export default function UploadClaimModal({
  showUploadClaimModal,
  setShowUploadClaimModal,
  getUploadClaimHistory,
}) {
  const [loadingFile, setLoadingFile] = useState(false);
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState(false);
  const [errors, setErrors] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const resetDragNdropRef = useRef(null);

  const handleRemoveFile = () => {
    formik.setFieldValue("file", null);
    if (resetDragNdropRef.current) {
      resetDragNdropRef.current.reset();
    }
  };

  const formik = useFormik({
    initialValues: { file: null },
    validationSchema: () =>
      Yup.object().shape({
        file: Yup.mixed()
          .required("Excel file required")
          .test(
            "fileFormat",
            t("Invalid file format. Please upload an Excel file."),
            (value) => {
              if (value instanceof File) {
                return (
                  value.type === "application/vnd.ms-excel" ||
                  value.type ===
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                );
              }
              return false;
            },
          )
          .test(
            "fileSize",
            t("File size is too large. Maximum size is 16MB."),
            (value) => {
              if (value instanceof File) {
                return value.size <= 16 * 1024 * 1024;
              }
              return false;
            },
          ),
      }),

    onSubmit: async (values) => {
      const token = localStorage.getItem("token");
      const tokenWithoutQuotes = token.replace(/"/g, "");
      if (!token) {
        console.error(t("Token not found in local storage."));
        return;
      }
      const formData = new FormData();
      formData.append("file", values.file);
      try {
        setLoadingFile(true);
        const response = await axios.post(
          "https://stage.api.connect.supergitsa.com/api/claim/excel",
          formData,
          {
            headers: {
              Authorization: `Bearer ${tokenWithoutQuotes}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        setLoadingFile(false);
        setUploadError(false);
        setErrors(null);
        setShowErrors(false);
        toast.success(response.data?.message);
        formik.resetForm();
        setShowUploadClaimModal(false);
        getUploadClaimHistory();
      } catch (error) {
        setLoadingFile(false);
        setUploadError(true);
        setErrors(error?.response?.data?.error);
        setShowErrors(false);
        console.log(error);
        toast.error(t("Upload failed. Click 'Get Errors' to see details."));
      }
    },
  });

  return (
    <>
      {/* ── Main Upload Modal ── */}
      <Transition.Root show={showUploadClaimModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[9999]"
          onClose={() => setShowUploadClaimModal(false)}
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <Dialog.Title className="text-sm font-bold text-[#1e293b] uppercase tracking-wide">
                      {t("Upload Claim")}
                    </Dialog.Title>
                    <button
                      onClick={() => setShowUploadClaimModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-opacity"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Progress Bar */}
                  {loadingFile && (
                    <LinearProgress
                      sx={{
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#0070C2",
                        },
                      }}
                    />
                  )}

                  {/* Body */}
                  <div className="p-4 space-y-3">
                    <DragNdrop
                      formik={formik}
                      width="100%"
                      height="100%"
                      onRemoveFile={handleRemoveFile}
                      ref={resetDragNdropRef}
                    />

                    {formik.errors.file && formik.touched.file && (
                      <p className="text-xs text-red-500 mt-1">
                        {formik.errors.file}
                      </p>
                    )}

                    {/* Error Detail Panel */}
                    {showErrors && errors && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <h4 className="text-red-700 font-semibold text-xs mb-1 uppercase">
                          {t("Errors")}:
                        </h4>
                        <p className="text-red-600 text-xs">{errors}</p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
                    {!uploadError ? (
                      <>
                        <AppButton
                          label={t("Cancel")}
                          variant="outlined"
                          onClick={() => setShowUploadClaimModal(false)}
                          className="!text-gray-600 !border-gray-300 hover:!bg-gray-100"
                        />
                        <AppButton
                          label={loadingFile ? "..." : t("Upload")}
                          disabled={loadingFile}
                          onClick={formik.handleSubmit}
                        />
                      </>
                    ) : (
                      <>
                        <AppButton
                          label={t("Get Errors")}
                          variant="outlined"
                          onClick={() => setShowErrors(true)}
                          className="!text-[#0070C2] !border-[#0070C2]"
                        />
                        <AppButton
                          label={t("Delete")}
                          onClick={() => setOpenConfirm(true)}
                          bgColor="#ef4444"
                        />
                      </>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* ── Delete Confirmation Modal ── */}
      <Transition.Root show={openConfirm} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[99999]"
          onClose={() => setOpenConfirm(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <Dialog.Title className="text-sm font-bold text-[#1e293b] uppercase tracking-wide flex items-center gap-2">
                      <IoWarningOutline className="text-red-500 text-lg" />
                      {t("Alert")}
                    </Dialog.Title>
                    <button
                      onClick={() => setOpenConfirm(false)}
                      className="text-gray-400 hover:text-gray-600 transition-opacity"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="px-4 py-5">
                    <p className="text-sm text-gray-700">
                      {t("Do you really want to delete uploaded file?")}
                    </p>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
                    <AppButton
                      label={t("Cancel")}
                      variant="outlined"
                      onClick={() => setOpenConfirm(false)}
                      className="!text-gray-600 !border-gray-300"
                    />
                    <AppButton
                      label={t("Yes, Delete")}
                      bgColor="#ef4444"
                      onClick={() => {
                        setUploadError(false);
                        setErrors(null);
                        setShowErrors(false);
                        formik.resetForm();
                        handleRemoveFile();
                        setOpenConfirm(false);
                      }}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
